import { useState, useEffect } from 'react'
import { Card, Row, Col, Form, Button, Alert, Spinner, Tabs, Tab, Table, Badge, Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import api, { provisioningApi } from '../services/api'

interface CertificateStatus {
  has_certificate: boolean
  certificate_type: string | null
  domain: string | null
  valid_from: string | null
  valid_until: string | null
  days_remaining: number | null
  issuer: string | null
}

interface AutoRenewalStatus {
  enabled: boolean
  script_exists: boolean
  last_log_entries: string[]
}

interface RPSCertificateInfo {
  exists: boolean
  certificate: string | null
  valid_from: string | null
  valid_until: string | null
  subject: string | null
  days_remaining: number | null
  server_url: string | null
  server_name: string
}

interface GlobalSettingsData {
  id?: string
  external_ip: string
  external_hostname: string
  external_port: number
  use_https: boolean
  default_smtp_host: string
  default_smtp_port: number
  default_smtp_user: string
  default_smtp_password?: string
  default_smtp_from_email: string
  default_smtp_from_name: string
  default_smtp_tls: boolean
  default_smtp_ssl: boolean
  provisioning_enabled: boolean
  provisioning_base_url: string
  // Yealink RPS
  rps_enabled: boolean
  rps_api_url: string
  rps_access_key: string
  rps_access_secret?: string
  rps_webhook_token?: string
  rps_webhook_token_set: boolean
  rps_auth_name?: string
  rps_auth_password?: string
  rps_configured: boolean
  password_min_length: number
  password_reset_expiry_hours: number
  // Alert/Monitoring
  alert_email: string
  trunk_monitor_enabled: boolean
  trunk_monitor_interval: number
}

const defaultSettings: GlobalSettingsData = {
  external_ip: '',
  external_hostname: '',
  external_port: 443,
  use_https: true,
  default_smtp_host: '',
  default_smtp_port: 587,
  default_smtp_user: '',
  default_smtp_password: '',
  default_smtp_from_email: '',
  default_smtp_from_name: 'it64Pbx',
  default_smtp_tls: true,
  default_smtp_ssl: false,
  provisioning_enabled: true,
  provisioning_base_url: '',
  // Yealink RPS
  rps_enabled: false,
  rps_api_url: 'https://dm.yealink.com',
  rps_access_key: '',
  rps_access_secret: '',
  rps_webhook_token: '',
  rps_webhook_token_set: false,
  rps_auth_name: '',
  rps_auth_password: '',
  rps_configured: false,
  password_min_length: 8,
  password_reset_expiry_hours: 24,
  // Alert/Monitoring
  alert_email: '',
  trunk_monitor_enabled: true,
  trunk_monitor_interval: 60
}

export default function GlobalSettings() {
  const [settings, setSettings] = useState<GlobalSettingsData>(defaultSettings)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testingEmail, setTestingEmail] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Certificate state
  const [certStatus, setCertStatus] = useState<CertificateStatus | null>(null)
  const [certLoading, setCertLoading] = useState(false)

  // RPS Webhook state
  const [webhookUrl, setWebhookUrl] = useState<string>('')
  // RPS Certificate state
  const [rpsCertInfo, setRpsCertInfo] = useState<RPSCertificateInfo | null>(null)
  const [rpsCertLoading, setRpsCertLoading] = useState(false)
  const [showRpsCertModal, setShowRpsCertModal] = useState(false)
  const [showLetsEncryptModal, setShowLetsEncryptModal] = useState(false)
  const [leDomain, setLeDomain] = useState('')
  const [leEmail, setLeEmail] = useState('')
  const [leRequesting, setLeRequesting] = useState(false)
  const [renewalStatus, setRenewalStatus] = useState<AutoRenewalStatus | null>(null)

  useEffect(() => {
    loadSettings()
    loadCertificateStatus()
  }, [])

  const loadSettings = async () => {
    try {
      const res = await api.get('/global-settings')
      setSettings({ ...defaultSettings, ...res.data })

      // Load webhook URL and RPS certificate if RPS is configured
      if (res.data.rps_configured) {
        loadWebhookUrl()
        loadRpsCertificate()
      }
    } catch (err) {
      // Settings might not exist yet, use defaults
      console.log('No settings found, using defaults')
    } finally {
      setLoading(false)
    }
  }

  const loadWebhookUrl = async () => {
    try {
      const res = await provisioningApi.getWebhookUrl()
      setWebhookUrl(res.data.webhook_url)
    } catch (err) {
      console.log('Could not load webhook URL')
    }
  }

  const loadRpsCertificate = async () => {
    setRpsCertLoading(true)
    try {
      const res = await api.get('/global-settings/rps-certificate')
      setRpsCertInfo(res.data)
    } catch (err) {
      console.log('Could not load RPS certificate info')
    } finally {
      setRpsCertLoading(false)
    }
  }

  const renewRpsCertificate = async () => {
    setRpsCertLoading(true)
    try {
      await api.post('/global-settings/rps-certificate/renew', null, { params: { years: 10 } })
      toast.success('RPS-Zertifikat wurde für 10 Jahre erneuert')
      await loadRpsCertificate()
    } catch (err) {
      toast.error('Fehler beim Erneuern des RPS-Zertifikats')
    } finally {
      setRpsCertLoading(false)
    }
  }

  const copyToClipboard = (text: string, message: string) => {
    navigator.clipboard.writeText(text)
    toast.success(message)
  }

  const loadCertificateStatus = async () => {
    setCertLoading(true)
    try {
      const [certRes, renewalRes] = await Promise.all([
        api.get('/global-settings/certificate-status'),
        api.get('/global-settings/auto-renewal-status')
      ])
      console.log('Certificate status response:', certRes.data)
      console.log('Renewal status response:', renewalRes.data)
      setCertStatus(certRes.data)
      setRenewalStatus(renewalRes.data)
    } catch (err) {
      console.error('Certificate status error:', err)
    } finally {
      setCertLoading(false)
    }
  }

  const handleRequestLetsEncrypt = async () => {
    if (!leDomain || !leEmail) {
      toast.error('Bitte Domain und E-Mail eingeben')
      return
    }
    setLeRequesting(true)
    try {
      await api.post('/global-settings/request-letsencrypt', {
        domain: leDomain,
        email: leEmail
      })
      toast.success('Let\'s Encrypt-Zertifikat erfolgreich eingerichtet!')
      setShowLetsEncryptModal(false)
      loadCertificateStatus()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler bei der Zertifikatsanfrage')
    } finally {
      setLeRequesting(false)
    }
  }

  const handleGenerateSelfSigned = async () => {
    setCertLoading(true)
    try {
      await api.post('/global-settings/generate-selfsigned')
      toast.success('Selbstsigniertes Zertifikat erstellt')
      loadCertificateStatus()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen')
    } finally {
      setCertLoading(false)
    }
  }

  const handleSetupAutoRenewal = async () => {
    try {
      await api.post('/global-settings/setup-auto-renewal')
      toast.success('Automatische Erneuerung eingerichtet')
      loadCertificateStatus()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler beim Einrichten')
    }
  }

  const handleDisableAutoRenewal = async () => {
    if (!window.confirm('Automatische Erneuerung wirklich deaktivieren?')) return
    try {
      await api.delete('/global-settings/auto-renewal')
      toast.success('Automatische Erneuerung deaktiviert')
      loadCertificateStatus()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler')
    }
  }

  const handleChange = (field: keyof GlobalSettingsData, value: string | number | boolean) => {
    setSettings(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.put('/global-settings', settings)
      toast.success('Einstellungen gespeichert')
    } catch (err) {
      toast.error('Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein')
      return
    }

    setTestingEmail(true)
    try {
      await api.post('/global-settings/test-email', { email: testEmail })
      toast.success('Test-E-Mail erfolgreich gesendet')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der Test-E-Mail')
    } finally {
      setTestingEmail(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>
          <i className="bi bi-sliders me-2"></i>
          Globale Einstellungen
        </h4>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Speichern...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-2"></i>
              Speichern
            </>
          )}
        </Button>
      </div>

      <Tabs defaultActiveKey="external" className="mb-4">
        <Tab eventKey="external" title={<><i className="bi bi-globe me-2"></i>Externe Erreichbarkeit</>}>
          <Card className="mb-4">
            <Card.Body>
              <Alert variant="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Diese Einstellungen werden für Provisioning, QR-Codes und Passwort-Reset-Links verwendet.
              </Alert>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Externe IP-Adresse</Form.Label>
                    <Form.Control
                      type="text"
                      value={settings.external_ip}
                      onChange={(e) => handleChange('external_ip', e.target.value)}
                      placeholder="z.B. 203.0.113.50"
                    />
                    <Form.Text className="text-muted">
                      Öffentliche IP-Adresse des Servers
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Externer Hostname</Form.Label>
                    <Form.Control
                      type="text"
                      value={settings.external_hostname}
                      onChange={(e) => handleChange('external_hostname', e.target.value)}
                      placeholder="z.B. pbx.meinefirma.de"
                    />
                    <Form.Text className="text-muted">
                      Wird bevorzugt verwendet, falls angegeben
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Port</Form.Label>
                    <Form.Control
                      type="number"
                      value={settings.external_port}
                      onChange={(e) => handleChange('external_port', parseInt(e.target.value) || 443)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Protokoll</Form.Label>
                    <Form.Select
                      value={settings.use_https ? 'https' : 'http'}
                      onChange={(e) => handleChange('use_https', e.target.value === 'https')}
                    >
                      <option value="https">HTTPS (empfohlen)</option>
                      <option value="http">HTTP</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Externe URL (Vorschau)</Form.Label>
                    <Form.Control
                      type="text"
                      readOnly
                      value={
                        settings.external_hostname || settings.external_ip
                          ? `${settings.use_https ? 'https' : 'http'}://${settings.external_hostname || settings.external_ip}${
                              (settings.use_https && settings.external_port === 443) ||
                              (!settings.use_https && settings.external_port === 80)
                                ? ''
                                : `:${settings.external_port}`
                            }`
                          : 'Nicht konfiguriert'
                      }
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="email" title={<><i className="bi bi-envelope me-2"></i>E-Mail (SMTP)</>}>
          <Card className="mb-4">
            <Card.Body>
              <Alert variant="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Diese SMTP-Einstellungen werden für Passwort-Reset-E-Mails und Systembenachrichtigungen verwendet.
              </Alert>

              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>SMTP Server</Form.Label>
                    <Form.Control
                      type="text"
                      value={settings.default_smtp_host}
                      onChange={(e) => handleChange('default_smtp_host', e.target.value)}
                      placeholder="z.B. smtp.gmail.com oder mail.meinefirma.de"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Port</Form.Label>
                    <Form.Control
                      type="number"
                      value={settings.default_smtp_port}
                      onChange={(e) => handleChange('default_smtp_port', parseInt(e.target.value) || 587)}
                    />
                    <Form.Text className="text-muted">
                      587 (TLS) oder 465 (SSL)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Benutzername</Form.Label>
                    <Form.Control
                      type="text"
                      value={settings.default_smtp_user}
                      onChange={(e) => handleChange('default_smtp_user', e.target.value)}
                      placeholder="z.B. noreply@meinefirma.de"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Passwort</Form.Label>
                    <div className="input-group">
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        value={settings.default_smtp_password || ''}
                        onChange={(e) => handleChange('default_smtp_password', e.target.value)}
                        placeholder="SMTP-Passwort"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Absender E-Mail</Form.Label>
                    <Form.Control
                      type="email"
                      value={settings.default_smtp_from_email}
                      onChange={(e) => handleChange('default_smtp_from_email', e.target.value)}
                      placeholder="noreply@meinefirma.de"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Absender Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={settings.default_smtp_from_name}
                      onChange={(e) => handleChange('default_smtp_from_name', e.target.value)}
                      placeholder="it64Pbx"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Verschlüsselung</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        id="smtp-tls"
                        label="STARTTLS (Port 587)"
                        checked={settings.default_smtp_tls && !settings.default_smtp_ssl}
                        onChange={() => {
                          handleChange('default_smtp_tls', true)
                          handleChange('default_smtp_ssl', false)
                        }}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        id="smtp-ssl"
                        label="SSL/TLS (Port 465)"
                        checked={settings.default_smtp_ssl}
                        onChange={() => {
                          handleChange('default_smtp_ssl', true)
                          handleChange('default_smtp_tls', false)
                        }}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        id="smtp-none"
                        label="Keine"
                        checked={!settings.default_smtp_tls && !settings.default_smtp_ssl}
                        onChange={() => {
                          handleChange('default_smtp_tls', false)
                          handleChange('default_smtp_ssl', false)
                        }}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <hr />

              <h6 className="mb-3">Test-E-Mail senden</h6>
              <Row>
                <Col md={6}>
                  <div className="input-group">
                    <Form.Control
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@beispiel.de"
                    />
                    <Button
                      variant="outline-primary"
                      onClick={handleTestEmail}
                      disabled={testingEmail || !settings.default_smtp_host}
                    >
                      {testingEmail ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Sende...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Test senden
                        </>
                      )}
                    </Button>
                  </div>
                  <Form.Text className="text-muted">
                    Speichern Sie zuerst die Einstellungen, bevor Sie testen.
                  </Form.Text>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="security" title={<><i className="bi bi-shield-lock me-2"></i>Sicherheit</>}>
          <Card className="mb-4">
            <Card.Body>
              <h6 className="mb-3">Passwort-Richtlinien</h6>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Minimale Passwortlänge</Form.Label>
                    <Form.Control
                      type="number"
                      min={6}
                      max={32}
                      value={settings.password_min_length}
                      onChange={(e) => handleChange('password_min_length', parseInt(e.target.value) || 8)}
                    />
                    <Form.Text className="text-muted">
                      Mindestens 6 Zeichen empfohlen
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Passwort-Reset-Link Gültigkeit (Stunden)</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={168}
                      value={settings.password_reset_expiry_hours}
                      onChange={(e) => handleChange('password_reset_expiry_hours', parseInt(e.target.value) || 24)}
                    />
                    <Form.Text className="text-muted">
                      Wie lange der Reset-Link gültig ist
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="monitoring" title={<><i className="bi bi-bell me-2"></i>Monitoring</>}>
          <Card className="mb-4">
            <Card.Body>
              <Alert variant="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Überwachung von Trunks und automatische E-Mail-Benachrichtigungen bei Problemen.
              </Alert>

              <h6 className="mb-3">
                <i className="bi bi-diagram-3 me-2"></i>
                Trunk-Überwachung
              </h6>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="trunk-monitor-enabled"
                  label="Trunk-Überwachung aktiviert"
                  checked={settings.trunk_monitor_enabled}
                  onChange={(e) => handleChange('trunk_monitor_enabled', e.target.checked)}
                />
                <Form.Text className="text-muted">
                  Überwacht den Registrierungsstatus aller Trunks und sendet Alarme bei Ausfall.
                </Form.Text>
              </Form.Group>

              {settings.trunk_monitor_enabled && (
                <>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prüf-Intervall (Sekunden)</Form.Label>
                        <Form.Control
                          type="number"
                          min={30}
                          max={600}
                          value={settings.trunk_monitor_interval}
                          onChange={(e) => handleChange('trunk_monitor_interval', parseInt(e.target.value) || 60)}
                        />
                        <Form.Text className="text-muted">
                          Wie oft der Status geprüft wird (Standard: 60 Sekunden)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Alarm E-Mail-Adresse</Form.Label>
                        <Form.Control
                          type="email"
                          value={settings.alert_email}
                          onChange={(e) => handleChange('alert_email', e.target.value)}
                          placeholder="admin@meinefirma.de"
                        />
                        <Form.Text className="text-muted">
                          E-Mail-Adresse für Alarm-Benachrichtigungen
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Alert variant="secondary" className="mb-0">
                    <strong>Funktionen:</strong>
                    <ul className="mb-0 mt-2">
                      <li><i className="bi bi-exclamation-triangle text-danger me-1"></i> <strong>Alarm</strong> wenn Trunk-Registrierung fehlschlägt</li>
                      <li><i className="bi bi-check-circle text-success me-1"></i> <strong>Entwarnung</strong> wenn Trunk wieder registriert ist</li>
                      <li><i className="bi bi-shield-check me-1"></i> Spam-Schutz: Nur eine E-Mail pro Ausfall/Wiederherstellung</li>
                    </ul>
                  </Alert>
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="provisioning" title={<><i className="bi bi-phone me-2"></i>Provisioning</>}>
          <Card className="mb-4">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="provisioning-enabled"
                  label="Telefon-Provisioning aktiviert"
                  checked={settings.provisioning_enabled}
                  onChange={(e) => handleChange('provisioning_enabled', e.target.checked)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Provisioning Base-URL (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={settings.provisioning_base_url}
                  onChange={(e) => handleChange('provisioning_base_url', e.target.value)}
                  placeholder="Falls abweichend von externer URL"
                />
                <Form.Text className="text-muted">
                  Leer lassen, um die externe URL zu verwenden
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <i className="bi bi-cloud me-2"></i>
              Yealink RPS (Redirect & Provisioning Service)
            </Card.Header>
            <Card.Body>
              <Alert variant="info" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Yealink RPS ermöglicht automatische Provisionierung von Yealink-Telefonen.
                Neue Telefone werden beim ersten Start automatisch zu diesem Server umgeleitet.
                <br /><br />
                <strong>Voraussetzungen:</strong>
                <ul className="mb-0 mt-2">
                  <li>Yealink Partner Account bei <a href="https://dm.yealink.com" target="_blank" rel="noopener noreferrer">dm.yealink.com</a></li>
                  <li>Access Key und Secret aus dem Yealink Device Management Portal</li>
                </ul>
              </Alert>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="rps-enabled"
                  label="Yealink RPS aktiviert"
                  checked={settings.rps_enabled}
                  onChange={(e) => handleChange('rps_enabled', e.target.checked)}
                />
              </Form.Group>

              {settings.rps_enabled && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>RPS API-URL</Form.Label>
                    <Form.Control
                      type="text"
                      value={settings.rps_api_url}
                      onChange={(e) => handleChange('rps_api_url', e.target.value)}
                      placeholder="https://api-dm.yealink.com/api/open/v1/device/redirect"
                    />
                    <Form.Text className="text-muted">
                      Standard-URL für Yealink RPS. Nur ändern, wenn von Yealink anders angegeben.
                    </Form.Text>
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Access Key ID</Form.Label>
                        <Form.Control
                          type="text"
                          value={settings.rps_access_key}
                          onChange={(e) => handleChange('rps_access_key', e.target.value)}
                          placeholder="Ihr Yealink Access Key"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Access Key Secret</Form.Label>
                        <Form.Control
                          type="password"
                          value={settings.rps_access_secret || ''}
                          onChange={(e) => handleChange('rps_access_secret', e.target.value)}
                          placeholder="Ihr Yealink Secret"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {settings.rps_configured && (
                    <>
                      <Alert variant="success">
                        <i className="bi bi-check-circle me-2"></i>
                        RPS ist konfiguriert. Sie können Telefone nun im Menü "Provisioning" → "MAC-Zuweisungen" bei RPS registrieren.
                      </Alert>

                      <Card className="mb-3 border-info">
                        <Card.Header className="bg-info text-white">
                          <i className="bi bi-link-45deg me-2"></i>
                          Event Subscription (Webhook)
                        </Card.Header>
                        <Card.Body>
                          <p className="text-muted mb-2">
                            Konfigurieren Sie diese URL in Ihrem Yealink RPS unter <strong>Event Subscription</strong>,
                            um Benachrichtigungen über Provisionierung, Alarme und Diagnosen zu erhalten:
                          </p>
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <code className="flex-grow-1 p-2 bg-light border rounded" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                              {webhookUrl || 'Lade...'}
                            </code>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                if (webhookUrl) {
                                  navigator.clipboard.writeText(webhookUrl)
                                  toast.success('Webhook-URL kopiert!')
                                }
                              }}
                              disabled={!webhookUrl}
                              title="In Zwischenablage kopieren"
                            >
                              <i className="bi bi-clipboard"></i>
                            </Button>
                          </div>
                          <div className="small text-muted mb-3">
                            <strong>Unterstützte Events:</strong>
                            <ul className="mb-0 mt-1">
                              <li><strong>provision</strong> - Gerät wurde erfolgreich provisioniert</li>
                              <li><strong>alarm</strong> - Gerätealarm (Netzwerk, Registrierung, etc.)</li>
                              <li><strong>diagnosis</strong> - Diagnosedaten vom Gerät</li>
                            </ul>
                          </div>

                          <hr />

                          <Form.Group className="mb-0">
                            <Form.Label>
                              <i className="bi bi-shield-lock me-1"></i>
                              Webhook Token (Sicherheit)
                              {settings.rps_webhook_token_set && (
                                <Badge bg="success" className="ms-2">Konfiguriert</Badge>
                              )}
                            </Form.Label>
                            <Form.Control
                              type="password"
                              value={settings.rps_webhook_token || ''}
                              onChange={(e) => handleChange('rps_webhook_token', e.target.value)}
                              placeholder={settings.rps_webhook_token_set ? '(Token ist gesetzt - leer lassen um beizubehalten)' : 'Token aus Yealink RPS Event Subscription'}
                            />
                            <Form.Text className="text-muted">
                              Geben Sie hier den Token ein, der im Yealink RPS bei der Event Subscription generiert wurde.
                              Eingehende Webhooks werden nur akzeptiert, wenn der Token übereinstimmt.
                            </Form.Text>
                          </Form.Group>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Provisioning Benutzername (authName)</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={settings.rps_auth_name || ''}
                                  onChange={(e) => handleChange('rps_auth_name', e.target.value)}
                                  placeholder="Optional: Benutzername für Provisioning"
                                  maxLength={32}
                                />
                                <Form.Text className="text-muted">
                                  HTTP Basic Auth Benutzername für die Provisionierung.
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Provisioning Passwort</Form.Label>
                                <Form.Control
                                  type="password"
                                  value={settings.rps_auth_password || ''}
                                  onChange={(e) => handleChange('rps_auth_password', e.target.value)}
                                  placeholder="Optional: Passwort für Provisioning"
                                  maxLength={32}
                                />
                                <Form.Text className="text-muted">
                                  HTTP Basic Auth Passwort für die Provisionierung.
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>

                      {/* RPS Server-Konfiguration für Yealink Portal */}
                      <Card className="mb-3 border-warning">
                        <Card.Header className="bg-warning text-dark">
                          <i className="bi bi-server me-2"></i>
                          Server-Konfiguration für Yealink RPS Portal
                        </Card.Header>
                        <Card.Body>
                          <Alert variant="warning" className="mb-3">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            <strong>Wichtig:</strong> Diese Informationen müssen im Yealink RPS Portal unter "Server" eingetragen werden,
                            damit die automatische Umleitung funktioniert.
                          </Alert>

                          {rpsCertLoading ? (
                            <div className="text-center py-3">
                              <Spinner animation="border" size="sm" className="me-2" />
                              Lade Zertifikat-Informationen...
                            </div>
                          ) : (
                            <>
                              <Table bordered size="sm" className="mb-3">
                                <tbody>
                                  <tr>
                                    <td style={{width: '180px'}}><strong>Server Name</strong></td>
                                    <td>
                                      <code>{rpsCertInfo?.server_name || 'it64Pbx PBX'}</code>
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 ms-2"
                                        onClick={() => copyToClipboard(rpsCertInfo?.server_name || 'it64Pbx PBX', 'Server Name kopiert!')}
                                      >
                                        <i className="bi bi-clipboard"></i>
                                      </Button>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td><strong>Server URL</strong></td>
                                    <td>
                                      <code>{rpsCertInfo?.server_url || 'Nicht konfiguriert'}</code>
                                      {rpsCertInfo?.server_url && (
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 ms-2"
                                          onClick={() => copyToClipboard(rpsCertInfo.server_url!, 'Server URL kopiert!')}
                                        >
                                          <i className="bi bi-clipboard"></i>
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td><strong>Username</strong></td>
                                    <td><em className="text-muted">(leer lassen)</em></td>
                                  </tr>
                                  <tr>
                                    <td><strong>Password</strong></td>
                                    <td><em className="text-muted">(leer lassen)</em></td>
                                  </tr>
                                  <tr>
                                    <td><strong>Custom Certificates</strong></td>
                                    <td><Badge bg="success">Enable</Badge></td>
                                  </tr>
                                </tbody>
                              </Table>

                              {/* Zertifikat-Bereich */}
                              <div className="border rounded p-3 bg-light">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <strong>
                                    <i className="bi bi-file-earmark-lock me-2"></i>
                                    Server Certificate (selbstsigniert, 10 Jahre gültig)
                                  </strong>
                                  <div>
                                    {rpsCertInfo?.exists && rpsCertInfo.days_remaining !== null && (
                                      <Badge bg={rpsCertInfo.days_remaining > 365 ? 'success' : rpsCertInfo.days_remaining > 30 ? 'warning' : 'danger'} className="me-2">
                                        Noch {rpsCertInfo.days_remaining} Tage gültig
                                      </Badge>
                                    )}
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      onClick={renewRpsCertificate}
                                      disabled={rpsCertLoading}
                                      title="Neues 10-Jahres-Zertifikat generieren"
                                    >
                                      <i className="bi bi-arrow-clockwise me-1"></i>
                                      {rpsCertInfo?.exists ? 'Erneuern' : 'Erstellen'}
                                    </Button>
                                  </div>
                                </div>

                                {rpsCertInfo?.exists && rpsCertInfo.certificate ? (
                                  <>
                                    <div className="small text-muted mb-2">
                                      Gültig: {rpsCertInfo.valid_from} bis {rpsCertInfo.valid_until}
                                    </div>
                                    <div className="d-flex gap-2 mb-2">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => setShowRpsCertModal(true)}
                                      >
                                        <i className="bi bi-eye me-1"></i>
                                        Zertifikat anzeigen
                                      </Button>
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={() => copyToClipboard(rpsCertInfo.certificate!, 'Zertifikat in Zwischenablage kopiert!')}
                                      >
                                        <i className="bi bi-clipboard me-1"></i>
                                        Kopieren
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => {
                                          const blob = new Blob([rpsCertInfo.certificate!], { type: 'application/x-pem-file' })
                                          const url = URL.createObjectURL(blob)
                                          const a = document.createElement('a')
                                          a.href = url
                                          a.download = 'rps-server-certificate.pem'
                                          a.click()
                                          URL.revokeObjectURL(url)
                                          toast.success('Zertifikat heruntergeladen!')
                                        }}
                                      >
                                        <i className="bi bi-download me-1"></i>
                                        Download .pem
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  <Alert variant="info" className="mb-0">
                                    <i className="bi bi-info-circle me-2"></i>
                                    Kein Zertifikat vorhanden. Klicken Sie auf "Erstellen" um ein neues 10-Jahres-Zertifikat zu generieren.
                                  </Alert>
                                )}
                              </div>
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    </>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="firewall" title={<><i className="bi bi-hdd-network me-2"></i>Firewall / Ports</>}>
          <Card className="mb-4">
            <Card.Body>
              <Alert variant="info" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Diese Ports müssen in der Firewall geöffnet sein, damit SIP-Telefonie, LDAP und Web-GUI funktionieren.
              </Alert>
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Dienst</th>
                    <th>Port</th>
                    <th>Protokoll</th>
                    <th>Beschreibung</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>SIP</strong></td>
                    <td>5060</td>
                    <td><Badge bg="primary">UDP</Badge> <Badge bg="secondary">TCP</Badge></td>
                    <td>Standard SIP-Signalisierung</td>
                  </tr>
                  <tr>
                    <td><strong>SIP TLS</strong></td>
                    <td>5061</td>
                    <td><Badge bg="secondary">TCP</Badge></td>
                    <td>Verschlüsselte SIP-Signalisierung</td>
                  </tr>
                  <tr>
                    <td><strong>RTP (Audio)</strong></td>
                    <td>10000-20000</td>
                    <td><Badge bg="primary">UDP</Badge></td>
                    <td>Medien-Streams (Sprache)</td>
                  </tr>
                  <tr>
                    <td><strong>AMI TLS</strong></td>
                    <td>5039</td>
                    <td><Badge bg="success">TCP/TLS</Badge></td>
                    <td>AMI verschlüsselt (empfohlen)</td>
                  </tr>
                  <tr>
                    <td><strong>LDAP</strong></td>
                    <td>389</td>
                    <td><Badge bg="secondary">TCP</Badge></td>
                    <td>Telefonbuch-Verzeichnis</td>
                  </tr>
                  <tr>
                    <td><strong>LDAPS</strong></td>
                    <td>636</td>
                    <td><Badge bg="secondary">TCP</Badge></td>
                    <td>Verschlüsseltes LDAP</td>
                  </tr>
                  <tr>
                    <td><strong>HTTP</strong></td>
                    <td>80</td>
                    <td><Badge bg="secondary">TCP</Badge></td>
                    <td>Web-GUI (Redirect zu HTTPS)</td>
                  </tr>
                  <tr>
                    <td><strong>HTTPS</strong></td>
                    <td>443</td>
                    <td><Badge bg="secondary">TCP</Badge></td>
                    <td>Web-GUI / Provisioning</td>
                  </tr>
                </tbody>
              </Table>
              <Alert variant="warning" className="mt-3 mb-0" style={{ fontSize: '0.85rem' }}>
                <i className="bi bi-shield-check me-2"></i>
                <strong>Empfehlung:</strong> Nutzen Sie nach Möglichkeit verschlüsselte Verbindungen (SIP TLS, LDAPS, HTTPS).
                Beschränken Sie RTP-Ports auf das Minimum (z.B. 10000-10100) je nach Anzahl gleichzeitiger Anrufe.
                Der AMI-Port sollte nur über TLS (Port 5039) erreichbar sein.
              </Alert>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="ssl" title={<><i className="bi bi-lock me-2"></i>SSL/TLS Zertifikate</>}>
          <Card className="mb-4">
            <Card.Body>
              <Alert variant="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                SSL/TLS-Zertifikate werden für die HTTPS-Verbindung der Web-GUI verwendet.
              </Alert>

              {certLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2 text-muted">Lade Zertifikatsstatus...</p>
                </div>
              ) : !certStatus ? (
                <Alert variant="danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Fehler beim Laden des Zertifikatsstatus. Bitte Seite neu laden.
                </Alert>
              ) : certStatus.has_certificate ? (
                <>
                  <h6 className="mb-3">Aktuelles Zertifikat</h6>
                  <Table bordered size="sm" className="mb-4">
                    <tbody>
                      <tr>
                        <td style={{ width: '200px' }}><strong>Typ</strong></td>
                        <td>
                          {certStatus.certificate_type === 'letsencrypt' ? (
                            <Badge bg="success"><i className="bi bi-patch-check me-1"></i>Let's Encrypt</Badge>
                          ) : (
                            <Badge bg="warning"><i className="bi bi-shield-exclamation me-1"></i>Selbstsigniert</Badge>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Domain/CN</strong></td>
                        <td>{certStatus.domain || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Aussteller</strong></td>
                        <td>{certStatus.issuer || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Gültig ab</strong></td>
                        <td>{certStatus.valid_from || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Gültig bis</strong></td>
                        <td>
                          {certStatus.valid_until || '-'}
                          {certStatus.days_remaining !== null && (
                            <Badge
                              bg={certStatus.days_remaining < 30 ? 'danger' : certStatus.days_remaining < 60 ? 'warning' : 'success'}
                              className="ms-2"
                            >
                              {certStatus.days_remaining} Tage verbleibend
                            </Badge>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>

                  {certStatus.certificate_type === 'letsencrypt' && (
                    <div className="mb-4">
                      <h6 className="mb-3">Automatische Erneuerung</h6>
                      {renewalStatus?.enabled ? (
                        <Alert variant="success">
                          <i className="bi bi-check-circle me-2"></i>
                          <strong>Aktiv</strong> - Zertifikat wird täglich um 3:00 Uhr geprüft und bei Bedarf erneuert.
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="float-end"
                            onClick={handleDisableAutoRenewal}
                          >
                            Deaktivieren
                          </Button>
                        </Alert>
                      ) : (
                        <Alert variant="warning">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Automatische Erneuerung ist nicht eingerichtet.
                          <Button
                            variant="success"
                            size="sm"
                            className="float-end"
                            onClick={handleSetupAutoRenewal}
                          >
                            <i className="bi bi-clock me-1"></i>
                            Auto-Erneuerung aktivieren
                          </Button>
                        </Alert>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <Alert variant="warning" className="mb-4">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Kein Zertifikat konfiguriert. AMI TLS ist möglicherweise nicht verfügbar.
                </Alert>
              )}

              <hr />

              <h6 className="mb-3">Zertifikat-Optionen</h6>
              <Row>
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <h6><i className="bi bi-patch-check me-2 text-success"></i>Let's Encrypt (empfohlen)</h6>
                      <p className="text-muted small">
                        Kostenloses, vertrauenswürdiges Zertifikat. Wird automatisch erneuert.
                        Erfordert eine öffentliche Domain und Port 80 Zugang.
                      </p>
                      <Button variant="success" onClick={() => setShowLetsEncryptModal(true)}>
                        <i className="bi bi-plus-lg me-1"></i>
                        Let's Encrypt einrichten
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <h6><i className="bi bi-shield-exclamation me-2 text-warning"></i>Selbstsigniert</h6>
                      <p className="text-muted small">
                        Für interne Nutzung. Browser/Clients zeigen Warnungen an.
                        Gültig für 10 Jahre, keine Erneuerung nötig.
                      </p>
                      <Button
                        variant="outline-warning"
                        onClick={handleGenerateSelfSigned}
                        disabled={certLoading}
                      >
                        {certLoading ? <Spinner size="sm" /> : (
                          <>
                            <i className="bi bi-arrow-repeat me-1"></i>
                            Neu generieren
                          </>
                        )}
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Let's Encrypt Modal */}
      <Modal show={showLetsEncryptModal} onHide={() => setShowLetsEncryptModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-patch-check text-success me-2"></i>
            Let's Encrypt Zertifikat
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Voraussetzungen:</strong>
            <ul className="mb-0 mt-2">
              <li>Domain muss auf diesen Server zeigen</li>
              <li>Port 80 muss vom Internet erreichbar sein</li>
              <li>certbot muss installiert sein</li>
            </ul>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Domain *</Form.Label>
            <Form.Control
              type="text"
              value={leDomain}
              onChange={(e) => setLeDomain(e.target.value)}
              placeholder="z.B. pbx.meinefirma.de"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>E-Mail für Benachrichtigungen *</Form.Label>
            <Form.Control
              type="email"
              value={leEmail}
              onChange={(e) => setLeEmail(e.target.value)}
              placeholder="admin@meinefirma.de"
            />
            <Form.Text className="text-muted">
              Let's Encrypt sendet Erinnerungen vor Ablauf des Zertifikats.
            </Form.Text>
          </Form.Group>

          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Der Webserver wird kurzzeitig gestoppt, um das Zertifikat anzufordern.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLetsEncryptModal(false)}>
            Abbrechen
          </Button>
          <Button
            variant="success"
            onClick={handleRequestLetsEncrypt}
            disabled={leRequesting || !leDomain || !leEmail}
          >
            {leRequesting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Anfrage läuft...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Zertifikat anfordern
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* RPS Certificate Modal */}
      <Modal show={showRpsCertModal} onHide={() => setShowRpsCertModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-file-earmark-lock text-warning me-2"></i>
            RPS Server-Zertifikat
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rpsCertInfo?.exists && rpsCertInfo.certificate && (
            <>
              <div className="mb-3">
                <strong>Gültigkeit:</strong> {rpsCertInfo.valid_from} bis {rpsCertInfo.valid_until}
                <Badge bg={rpsCertInfo.days_remaining && rpsCertInfo.days_remaining > 365 ? 'success' : 'warning'} className="ms-2">
                  Noch {rpsCertInfo.days_remaining} Tage
                </Badge>
              </div>
              <Form.Group>
                <Form.Label>Zertifikat (PEM-Format) - kopieren und im Yealink RPS Portal einfügen:</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={15}
                  value={rpsCertInfo.certificate}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => {
              if (rpsCertInfo?.certificate) {
                copyToClipboard(rpsCertInfo.certificate, 'Zertifikat kopiert!')
              }
            }}
          >
            <i className="bi bi-clipboard me-1"></i>
            In Zwischenablage kopieren
          </Button>
          <Button variant="secondary" onClick={() => setShowRpsCertModal(false)}>
            Schliessen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
